import { inject, Injectable } from "@angular/core";
import { CellType, ICellPosition, IgxColumnComponent, IgxGridComponent } from "@infragistics/igniteui-angular";
import
{
    ClassMetadata,
    GridEditType,
    IGridViewMetadata,
    KeyValuePair,
    PropertyMetadata
} from "@tylertech/tyler-api-types";
import { GridMetadataService } from "./grid.metadata.service";
import { Utilities } from "@tylertech/shared-module";
import { AugmentedModelObject, MetadataKey, RepositoryFormData } from "../../interfaces";
import { ObjectHelpers } from "../../utilities";
import { MetadataService } from "../metadata.service";
import { MetadataPropertyService } from "../metadataPropertyService";
import { MetadataInputTypeHelperService } from "../metadataInputTypeHelperService";
import { GridLoadService } from "./grid.load.service";
import { ProviderStorageService } from "./provider.storage.service";
import { ResetStateService } from "../reset.state.service";
import { NavigationService } from "@tylertech/core-module";

@Injectable({ providedIn: "root" })
export class CellEditService extends ResetStateService
{

    CellEditCancel: Record<string, boolean> = {};

    CellEditAfterAdd: Record<string, boolean> = {};

    CellEditCell: Record<string, CellType> = {};

    ViewOnly: Record<string, boolean> = {};

    InvalidCells: Record<string, boolean> = {};

    FreeEntryValue: boolean;

    constructor(
        private readonly $metadataService: MetadataService,
        private $providerStorageService: ProviderStorageService,
        private readonly $metadataPropertyService: MetadataPropertyService,
        private $metadataInputTypeHelperService: MetadataInputTypeHelperService,
        private $gridLoadService: GridLoadService,
        private $gridMetadataService: GridMetadataService
    )
    {
        super(inject(NavigationService));
    }

    ActivateEditableCell(grid: IgxGridComponent, followingPosition: ICellPosition, enterEditMode: boolean = false): void
    {
        grid.navigateTo(
            followingPosition.rowIndex,
            followingPosition.visibleColumnIndex,
            args => {
                args.target.activate();
                grid.cdr.detectChanges();
            }
        );
        if (enterEditMode)
            grid.crudService.enterEditMode(grid.getCellByColumn(followingPosition.rowIndex, grid.visibleColumns[followingPosition.visibleColumnIndex].field));
    }

    VerticalEditNavigationAllowed(keyCode: string, rows: any[], currentRowIndex: number, inputType: string): boolean
    {
        if (this.$metadataInputTypeHelperService.isSelect(inputType) || this.$metadataInputTypeHelperService.isMultiSelect(inputType)
                || this.$metadataInputTypeHelperService.isTypeAhead(inputType) || this.$metadataInputTypeHelperService.isDate(inputType)
                || this.$metadataInputTypeHelperService.isDateTime(inputType) || this.$metadataInputTypeHelperService.isTime(inputType)
                || this.$metadataInputTypeHelperService.isTime24(inputType))
            return false;
        if ((keyCode === "ArrowUp" && currentRowIndex === 0) || (keyCode !== "ArrowUp" && currentRowIndex === (rows.length - 1)))
            return false;
        return true;
    }

    EditNextVerticalCell(
        grid: IgxGridComponent,
        viewMetadata: IGridViewMetadata,
        rowIndex: number,
        columnIndex: number,
        up: boolean
    ): void
    {
        const newRowIndex = up ? rowIndex - 1 : rowIndex + 1;

        if (up && newRowIndex < 0)
            return;
        else if (!up && newRowIndex >= grid.data.length)
            return;

        const field = grid.columns[columnIndex].field;

        if (!this.AllowCellEdit(grid, viewMetadata, columnIndex, field, grid.getCellByColumn(newRowIndex, field), false))
        {
            this.EditNextVerticalCell(grid, viewMetadata, newRowIndex, columnIndex, up);
        }
        else
        {
            const position = <ICellPosition>{ rowIndex: newRowIndex, visibleColumnIndex: grid.columns[columnIndex].visibleIndex };
            this.ActivateEditableCell(grid, position);
        }
    }

    EditFirstCell(
        grid: IgxGridComponent,
        viewMetadata: IGridViewMetadata,
        _rowIndex?: number,
        enterEditMode: boolean = false
    ): void
    {
        const rowIndex = !Utilities.IsNullOrUndefined(_rowIndex)
            ? _rowIndex
            : viewMetadata.GridProperties.AddToTop || grid.data.length === 0
                ? 0
                : grid.data.length - 1;
        const position = this.GetFirstEditableCell(grid, viewMetadata, rowIndex, 0);
        if (Utilities.IsNullOrUndefined(position))
            return;
        this.ActivateEditableCell(grid, position, enterEditMode);
    }


    AllowCellEdit(grid: IgxGridComponent, viewMetadata: IGridViewMetadata, currentColumnIndex: number, columnName: string, cell?: CellType, tryEditNext: boolean = true): boolean
    {
        const providerId = viewMetadata?.GridProperties["$providerId"];
        if (!this.AllowCellEditType(columnName, viewMetadata))
            return false;
        const index = cell ? cell.row?.index : this.CellEditCell[providerId]?.row?.index;
        if (Utilities.IsNullOrUndefined(index))
            return true;
        if (Utilities.IsNullOrUndefined(this.ViewOnly[columnName]) || !Utilities.IsNullOrUndefined(cell) || !viewMetadata.GridProperties.CanEdit)
        {
            const cell = grid.getCellByColumn(index, columnName);
            const columns = viewMetadata["$columns"];
            const viewOnly = this.ViewOnlyCell(
                cell,
                grid,
                this.$providerStorageService.GridData[providerId],
                viewMetadata,
                columns
            );
            if (Utilities.IsNullOrUndefined(currentColumnIndex))
                return false;
            if (tryEditNext && viewOnly && !this.ViewOnly[columnName] && viewMetadata.GridProperties.CanEdit && grid.columns[currentColumnIndex].field === columnName)
                this.ActivateEditableCell(grid, this.GetNextEditableCellSafe(grid, viewMetadata, currentColumnIndex, index, grid.columns[currentColumnIndex].visibleIndex));
            this.ViewOnly[columnName] = viewOnly;
        }
        return !this.ViewOnly[columnName];
    }

    IsAfterLastEditableColumn(grid: IgxGridComponent, viewMetadata: IGridViewMetadata, rowIndex: number, columnIndex: number): boolean
    {
        const field = grid.visibleColumns[columnIndex].field;
        const cell = grid.getCellByColumn(rowIndex, field);
        const editableColumns = grid.visibleColumns.filter(x => this.AllowCellEdit(grid, viewMetadata, columnIndex, x.field, cell, false));

        const lastEditableColumnName = editableColumns[editableColumns.length - 1].field;
        const lastEditableColumnCell = grid.getCellByColumn(rowIndex, lastEditableColumnName).column;

        return lastEditableColumnCell.visibleIndex <= columnIndex;
    }

    GetNextEditableCellSafe(grid: IgxGridComponent, viewMetadata: IGridViewMetadata, currentColumnIndex: number, rowIndex: number, columnIndex: number): ICellPosition
    {
        const isAtLastColumn = this.IsAfterLastEditableColumn(grid, viewMetadata, rowIndex, columnIndex);
        const isLastRow = rowIndex === grid.filteredSortedData.length - 1;
        let rowIndexForPermission = rowIndex;
        if (isAtLastColumn && !isLastRow)
            rowIndexForPermission = rowIndex + 1;
        if (isAtLastColumn && isLastRow)
            return null;
        return this.GetNextEditableCell(grid, viewMetadata, currentColumnIndex, rowIndex, columnIndex, rowIndexForPermission);
    }

    GetPreviousEditableCell(grid: IgxGridComponent, viewMetadata: IGridViewMetadata, currentColumnIndex: number, rowIndex: number, columnIndex: number, rowIndexForPermission: number)
    {
        return grid.getPreviousCell(
            rowIndex,
            columnIndex,
            (column: IgxColumnComponent) => this.AllowCellEdit(grid, viewMetadata, currentColumnIndex, column.field, grid.getCellByColumn(rowIndexForPermission, column.field), false)
        );
    }

    GetNextEditableCell(grid: IgxGridComponent, viewMetadata: IGridViewMetadata, currentColumnIndex: number, rowIndex: number, columnIndex: number, rowIndexForPermission: number)
    {
        return grid.getNextCell(
            rowIndex,
            columnIndex,
            (column: IgxColumnComponent) => this.AllowCellEdit(grid, viewMetadata, currentColumnIndex, column.field, grid.getCellByColumn(rowIndexForPermission, column.field), false)
        );
    }

    GetFirstEditableCell(grid: IgxGridComponent, viewMetadata: IGridViewMetadata, rowIndex: number, columnIndex: number = 0): ICellPosition
    {
        //TODO: If we get to last column and no editable cells, should we move down a row and keep trying or end it?
        const column = grid.getColumnByVisibleIndex(columnIndex);
        if (!column)
            return null;
        const cell = grid.getCellByColumn(rowIndex, column.field);
        if (!cell)
            return null;
        if (!this.AllowCellEdit(grid, viewMetadata, columnIndex, column.field, cell, false))
        {
            return this.GetFirstEditableCell(
                grid,
                viewMetadata,
                rowIndex,
                columnIndex + 1
            );
        }
        return <ICellPosition>{ rowIndex: rowIndex, visibleColumnIndex: columnIndex };
    }

    IsLastEditableColumn(grid: IgxGridComponent, viewMetadata: IGridViewMetadata, rowIndex: number, columnIndex: number): boolean
    {
        const field = grid.visibleColumns[columnIndex].field;
        const cell = grid.getCellByColumn(rowIndex, field);
        const editableColumns = grid.visibleColumns.filter(x => this.AllowCellEdit(grid, viewMetadata, columnIndex, x.field, cell, false));
        return field === editableColumns[editableColumns.length - 1].field;
    }

    ViewOnlyCell(
        cell: CellType,
        grid: IgxGridComponent,
        rows: any[],
        viewMetadata: IGridViewMetadata,
        columns: any[]
    ): boolean
    {
        if (!cell) return true;
        if (!rows) return false;

        if (!this.isAllowedCellEditType(this.$gridMetadataService.getProperty(viewMetadata.GridProperties.ClassMetadata, cell.column.field)?.InputType))
            return true;
        const row = rows.find(x => x.Id === cell.row.key);
        if (Utilities.IsNullOrUndefined(row))
            return false;
        if (columns.filter(x => x.Name === viewMetadata.GridProperties.CanEditProperty).length === 1)
        {
            if (row[columns.filter(x => x.Name === viewMetadata.GridProperties.CanEditProperty)[0].Name]?.toString().toLowerCase() !== "true")
                return true;
        }
        const model = row["$$model"];

        if (!viewMetadata.GridProperties.CanEdit && !model["$addedCellEdit"])
            return true;

        const flattenedProperties = this.$metadataService.FlattenProperties(
            viewMetadata.GridProperties.ClassMetadata,
            model.$$Parent,
            model
        );
        const property = grid.columns.find(x => x.index === cell.id.columnID).field;
        const propertyMetadata = flattenedProperties.find(x => x.name === property).property;
        return this.$metadataPropertyService.isViewOnly(propertyMetadata, model, model["$$Parent"]);
    }

    AllowCellEditType(columnName: string, viewMetadata: IGridViewMetadata): boolean
    {
        return (
            !!viewMetadata &&
            !viewMetadata.ViewOnly &&
            viewMetadata.EditType === GridEditType.Cell &&
            (viewMetadata.GridProperties.CanEdit || viewMetadata.GridProperties.CanAdd) &&
            this.isAllowedCellEditType(
                this.$gridMetadataService.getProperty(viewMetadata.GridProperties.ClassMetadata, columnName)?.InputType
            )
        );
    }

    private isAllowedCellEditType(type: string): boolean
    {
        return (
            this.$metadataInputTypeHelperService.isButton(type) ||
            this.$metadataInputTypeHelperService.isText(type) ||
            this.$metadataInputTypeHelperService.isSelect(type) ||
            this.$metadataInputTypeHelperService.isMultiSelect(type) ||
            this.$metadataInputTypeHelperService.isDate(type) ||
            this.$metadataInputTypeHelperService.isTime(type) ||
            this.$metadataInputTypeHelperService.isTime24(type) ||
            this.$metadataInputTypeHelperService.isDateTime(type) ||
            this.$metadataInputTypeHelperService.isCheck(type) ||
            this.$metadataInputTypeHelperService.isNumber(type) ||
            this.$metadataInputTypeHelperService.isNumberWithoutDisplayFiltering(type) ||
            this.$metadataInputTypeHelperService.isCurrency(type) ||
            this.$metadataInputTypeHelperService.isPercent(type) ||
            this.$metadataInputTypeHelperService.isPhoneNumber(type) ||
            this.$metadataInputTypeHelperService.isPhoneNumberWithExtension(type) ||
            this.$metadataInputTypeHelperService.isTypeAhead(type)
        );
    }

    RecalculateAfterCellEdit(model: any, classMetadata: ClassMetadata, property: string, originalModelValue: any, force: boolean, formData: RepositoryFormData): void
    {
        const newValue = model[property];

        const propertyMetadata = classMetadata.Properties.find(p => p.Name === property);
        if (propertyMetadata.RecalculateOnChange &&
            (force || this.$metadataInputTypeHelperService.delayRecalculateForCellEdit(propertyMetadata.InputType))
        )
        {
            model[property] = originalModelValue;
            this.$metadataPropertyService.recalculateProperties(
                classMetadata.TypeName,
                propertyMetadata,
                model,
                model.$$Parent,
                newValue,
                undefined,
                false,
                false,
                formData
            );
            model[property] = newValue;
        }
    }

    AfterCellEdit(
        cell: CellType,
        row: any,
        propertyMetadata: PropertyMetadata,
        viewMetadata: IGridViewMetadata,
        grid: IgxGridComponent,
        providerId: string,
        newModelValue: any
    ): void
    {
        const self = this;
        row["$$model"][propertyMetadata.Name] = newModelValue;
        this.$gridLoadService.fireModelChangedListeners(providerId);
        row[propertyMetadata.Name] = newModelValue;
        if (Utilities.IsNullOrUndefined(row[propertyMetadata.Name]))
            updateGridCell();
        else if (!propertyMetadata.LookupProperty && propertyMetadata.Lookup && self.$metadataInputTypeHelperService.isLookup(propertyMetadata.InputType))
        {
            if (propertyMetadata.FreeEntry && this.FreeEntryValue)
            {
                this.FreeEntryValue = false;
                updateGridCell();
            }
            else
            {
                let ids: string[] = [];
                if (self.$metadataInputTypeHelperService.isMultiSelect(propertyMetadata.InputType))
                    ids = row[propertyMetadata.Name].toString().split(",");
                else
                    ids.push(row[propertyMetadata.Name].toString());
                self.$gridLoadService
                    .LookupValueForTable(
                        propertyMetadata,
                        self.$providerStorageService.GridData[providerId],
                        ids
                    )
                    .then((x) =>
                    {
                        updateGridCell();
                    });
            }
        }
        else if (
            propertyMetadata.LookupProperty &&
            self.$metadataInputTypeHelperService.isLookup(propertyMetadata.InputType)
        )
        {
            let ids: string[] = [];
            if (self.$metadataInputTypeHelperService.isMultiSelect(propertyMetadata.InputType))
                ids = row[propertyMetadata.Name].toString().split(",");
            else
                ids.push(row[propertyMetadata.Name].toString());
            const list: KeyValuePair<MetadataKey, string>[] = self.$metadataService.DependentModuleValue(
                propertyMetadata.LookupProperty,
                row["$$model"]["$$Parent"],
                row["$$model"]
            );
            if (Utilities.IsNullOrUndefinedOrEmpty(list))
                return;
            const values = [];
            ids.forEach(id =>
            {
                values.push(list.find(x => x.Key.toString() === id.toString())?.Value);
            });
            row[propertyMetadata.Name] = Utilities.IsNullOrUndefinedOrEmpty(values) ? "" : values.join(",");
            updateGridCell();
        }
        else
        {
            updateGridCell();
        }
        function updateGridCell(): void
        {
            if (!cell)
                return;
            cell.editValue = row[propertyMetadata.Name];
            if (propertyMetadata.InputType === "PhoneNumber" || propertyMetadata.InputType === "Number")
                //Phone stores as unformatted
                row[propertyMetadata.Name] = self.$metadataService.getFormattedValueFromProp(
                    newModelValue,
                    propertyMetadata
                );
            grid.cdr.detectChanges();
        }
    }

    SetCellEditValidity(providerId: string, model: AugmentedModelObject, property: string, isInvalid: boolean): void
    {
        if (!model["$cellEditInvalidProperties"])
            model["$cellEditInvalidProperties"] = [];
        const index = model["$cellEditInvalidProperties"].indexOf(property);
        if (isInvalid)
        {
            if (index === -1)
                model["$cellEditInvalidProperties"].push(property);
        }
        else
        {
            if (index > -1)
                model["$cellEditInvalidProperties"].splice(index, 1);
        }
        this.InvalidCells[providerId] = model["$cellEditInvalidProperties"].length > 0;
    }

    ClearCellEditErrors(model: AugmentedModelObject): void
    {
        this.SetCellEditErrors(model, true);
    }

    SetCellEditErrors(model: AugmentedModelObject, clear: boolean = false): void
    {
        let modelToUse: AugmentedModelObject = model;
        if (Utilities.IsNullOrUndefined(model["$cellEditInvalidProperties"]) && model.$$Parent)
            modelToUse = model.$$Parent;
        if (Utilities.IsNullOrUndefined(modelToUse["$cellEditInvalidProperties"]))
            return;
        if (!modelToUse.$errors || clear)
            modelToUse.$errors = {};
        modelToUse["$cellEditInvalidProperties"].forEach(property =>
        {
            ObjectHelpers.safeAccess(modelToUse.$errors, property, {})["serverVerification"] = `${property} is invalid`;
        });
    }
}
